/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef } from 'react';
import {
    Link as RouterLink,
    NavLink as RouterNavLink,
    type LinkProps as RouterLinkProps,
    type NavLinkProps as RouterNavLinkProps,
} from 'react-router';
import { buildUrl, useSite } from '@salesforce/storefront-next-runtime/site-context';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useCurrentSiteAndLocaleRef } from '@/hooks/use-current-site-and-locale-ref';
import { isLegacyRoute } from '@/lib/legacy-routes';
import type { AppConfig } from '@/types/config';

/**
 * Links to `hybrid.legacyRoutes` (e.g. /checkout → SFRA) must be plain document navigations.
 *
 * Letting React Router start a client navigation and relying on the legacy-routes middleware
 * to set `window.location.href` is racy: React Router loads the target route modules in
 * parallel, Firefox aborts those in-flight imports once the document navigation starts, and
 * React Router reacts to the failed import with `window.location.reload()` — reloading the
 * *current* page and cancelling the legacy navigation. With `reloadDocument` no route module
 * is ever requested. The unprefixed path is used, matching what the middleware navigates to.
 */
function useLegacyRouteOverride(to: RouterLinkProps['to']): Pick<RouterLinkProps, 'to' | 'reloadDocument'> | null {
    const config = useConfig<AppConfig>();
    return typeof to === 'string' && isLegacyRoute(to, config) ? { to, reloadDocument: true } : null;
}

/**
 * Site-context-aware <Link>. Drop-in replacement for React Router's <Link>.
 * Automatically prepends URL prefix and appends search params from Url config.
 * When no SiteProvider is mounted, behaves identically to React Router's Link.
 */
export const Link = forwardRef<HTMLAnchorElement, RouterLinkProps>(function Link({ to: _to, ...rest }, ref) {
    const { site } = useSite();
    const config = useConfig();
    const { siteRef, localeRef } = useCurrentSiteAndLocaleRef();

    const to =
        typeof _to === 'string' && site
            ? buildUrl({
                  to: _to,
                  urlConfig: config.url,
                  params: { siteId: siteRef, localeId: localeRef },
              })
            : _to;
    const legacy = useLegacyRouteOverride(_to);
    return <RouterLink ref={ref} to={to} {...rest} {...legacy} />;
});

/**
 * Site-context-aware <NavLink>. Drop-in replacement for React Router's <NavLink>.
 * Inherits all NavLink functionality (active class, aria-current).
 */
export const NavLink = forwardRef<HTMLAnchorElement, RouterNavLinkProps>(function NavLink({ to: _to, ...rest }, ref) {
    const { site } = useSite();
    const config = useConfig();
    const { siteRef, localeRef } = useCurrentSiteAndLocaleRef();
    const to =
        typeof _to === 'string' && site
            ? buildUrl({
                  to: _to,
                  urlConfig: config.url,
                  params: { siteId: siteRef, localeId: localeRef },
              })
            : _to;
    const legacy = useLegacyRouteOverride(_to);
    return <RouterNavLink ref={ref} to={to} {...rest} {...legacy} />;
});
